import { MegaphoneIcon } from '@heroicons/react/24/outline'
import moment from 'moment'
import { ReactElement } from 'react'
import { CommLogEntry } from './CommLogEntry'
import type { CommLogEntryFields } from 'app/types/comm-log'
import { ActivityCard } from 'app/components/activities/ActivityCard'
import { summarizeCommLogChannels } from 'app/lib/commLog'

/**
 * A collapsed stretch of the practice's automated reminder traffic. On the
 * contacts sampled this is the overwhelming majority of the comm log, so it
 * folds away by default. Nothing is dropped: the run expands in place.
 *
 * Unlike a ledger run this keeps its disclosure. A day holds two or three
 * transactions; a run here can hold ninety reminders, and putting ninety rows
 * on the page is the noise the run exists to remove.
 */
export function AutomatedRun({ entries }: { entries: CommLogEntryFields[] }): ReactElement {
  const newest = entries[0]?.commDate
  const oldest = entries[entries.length - 1]?.commDate
  const range =
    newest && oldest ? `${moment(oldest).format(`MMM D, YYYY`)} to ${moment(newest).format(`MMM D, YYYY`)}` : `Undated`

  return (
    <ActivityCard
      icon={<MegaphoneIcon className="h-5 w-5 shrink-0 rounded-md bg-slate-400 p-1 text-on-color" />}
      title={
        <div className="flex min-w-0 flex-wrap items-center gap-x-1.5 text-ui-sm text-strong">
          <span className="shrink-0 text-body-xs uppercase tracking-wide text-minimal">PMS</span>
          <span>{`${entries.length} automated messages`}</span>
          <span className="truncate text-body-xs text-subtle">{summarizeCommLogChannels(entries)}</span>
        </div>
      }
      isFirst
      isLast
      preview={
        <div className="w-full rounded-lg border border-default bg-surface px-4 py-3 text-body-xs shadow-sm">
          <span className="text-subtle">{range}</span>
        </div>
      }
      drawer={
        <ul className="flex flex-col">
          {entries.map((entry) => (
            <li key={entry.id}>
              <CommLogEntry entry={entry} />
            </li>
          ))}
        </ul>
      }
    />
  )
}
